// [claude-code 2026-04-10] S8-T4: Cross-agent Solvys chat drawer via surface.sidebar SSE
// [claude-code 2026-04-05] T2: Chat icons moved to Consilium bar — event-driven new chat, run report, load session
// [claude-code 2026-03-28] S8-T7: Single-pane sidebar with agent-plan inline
// [claude-code 2026-04-19] New chat / run report / load session now animated via View Transitions API
// S13-T1: Renamed to ChatSidebar, surfaceId=chat
import { useCallback, useState, useEffect, useRef, useMemo } from "react";
import {
  AssistantRuntimeProvider,
  useThread,
  useThreadRuntime,
} from "@assistant-ui/react";
import { Play } from "lucide-react";
import { useFintheonAgents } from "../../contexts/FintheonAgentContext";
import { useFintheonRuntime } from "./useFintheonRuntime";
import type { QueuedRunContext } from "./hooks/useFintheonChat";
import type { QueuedMessage } from "./MessageQueue";
import { useToast } from "../../contexts/ToastContext";
import { FintheonThread } from "./FintheonThread";
import { FintheonComposer } from "./FintheonComposer";
import { TodoDrawer } from "./TodoDrawer";
import { useTodoList } from "./hooks/useTodoList";
import { useMessageQueue } from "./hooks/useMessageQueue";
import {
  buildChatWorkStorageKey,
  conversationWorkStoragePart,
} from "./hooks/workStorageScope";
import {
  useChatUiActions,
  type ChatUiRightRailPayload,
  type ChatUiTodoItem,
} from "./hooks/useChatUiActions";
import { useAutoCollapseDrawer } from "./hooks/useAutoCollapseDrawer";
import { ChatQuestionApprovalDrawer } from "./ChatQuestionApprovalDrawer";
import { ChatPlanApprovalQuestionDrawer } from "./ChatPlanApprovalQuestionDrawer";
import { ChatDrawerPeek } from "./ChatDrawerPeek";
import { ChatCanvasActionStrip } from "./ChatCanvasActionStrip";
import { StreamdownChat } from "./slots";
import { normalizeReasoningLevel, shouldThinkHarder } from "./reasoning";
import type { ReasoningLevel } from "./reasoning";
import { useAgentBusSSE } from "../../hooks/useAgentBusSSE";
import { withViewTransition } from "../../lib/view-transition";
import { getBrowserApiBase } from "../../lib/browser-api-base";
import {
  fetchAgenticProjection,
  type AgenticProjection,
} from "../../lib/agentic/task-api";
import {
  normalizeAgenticTaskMode,
  type AgenticTaskMode,
} from "../../lib/agentic/task-mode";
import { resetActiveHarperProviderToDefault } from "./provider-options";
import { SKILL_PREFIXES } from "../../lib/skillPrefixes";
import { writeAttachedConnectorIds } from "../../lib/chat-connector-attachments";
import { AgenticDraftCards } from "./agentic/AgenticDraftCards";
import {
  SolvysChatDrawer,
  type SolvysChatDrawerMessage,
} from "./SolvysChatDrawer";
import type { SidebarNotifyEvent } from "../../../backend-hono/src/services/agent-bus/types";
import { getAccessToken } from "../../lib/supabase";
import {
  CHAT_CONVERSATION_DELETED_EVENT,
  readDeletedConversationId,
} from "./conversation-events";

const API_BASE = getBrowserApiBase();

/** Default display names — harper entry overridden at runtime by CAO name from agent context */
const AGENT_NAMES_DEFAULT: Record<string, string> = {
  oracle: "Castra",
  feucht: "Feucht",
  consul: "Consul",
  herald: "Herald",
  harper: "Harper",
};

/** Maps agent IDs to FintheonAgent IDs for "currently chatting with" suppression. */
const AGENT_TO_FINTHEON_ID: Record<string, string> = {
  harper: "harper",
  oracle: "oracle",
  feucht: "feucht",
  consul: "consul",
  herald: "herald",
};

const PLAN_DRAFT_KEY = "fintheon:plan-markdown-v1";
const PLAN_DRAFT_TEMPLATE = `# Plan\n\n## Objectives\n- [ ] \n\n## Steps\n1. \n\n## Notes\n`;

function queueItemContext(item: QueuedMessage): QueuedRunContext {
  return {
    ...(item.images?.length ? { images: item.images } : {}),
    ...(item.referenceContext
      ? { referenceContext: item.referenceContext }
      : {}),
    ...(item.riskFlowContext ? { riskFlowContext: item.riskFlowContext } : {}),
  };
}

type AgentRailSurface = "plan" | "canvas" | "browser" | "report";

function toAgentRailSurface(
  surface: ChatUiRightRailPayload["surface"],
): AgentRailSurface {
  if (surface === "canvas" || surface === "browser") return surface;
  if (surface === "report") return "report";
  return "plan";
}

interface AgentRailEventDetail {
  open?: boolean;
  markdown?: string;
  append?: boolean;
  title?: string;
  surface?: AgentRailSurface;
}

function ChatSidebarInner({
  lastError,
  lastRequestId,
  thinkHarder,
  setThinkHarder,
  reasoningLevel,
  setReasoningLevel,
  conversationId,
  connectorScopeId,
  setConversationId,
  clearConversationId,
  compact = true,
  mode,
  onModeChange,
  planMarkdown,
  railTitle,
  railSurface,
  agenticMode,
  onAgenticModeChange,
  lastAgenticTaskId,
  isLoading,
  thinkingContent,
  pausedTaskId,
  pausingTaskId,
  onResumeActiveRun,
  onQueueActiveRun,
  onSteerActiveRun,
  onUpdateQueuedRun,
  onRemoveQueuedRun,
}: {
  lastError: string | null;
  lastRequestId: string | null;
  thinkHarder: boolean;
  setThinkHarder: (v: boolean) => void;
  reasoningLevel: ReasoningLevel;
  setReasoningLevel: (level: ReasoningLevel) => void;
  conversationId: string | undefined;
  connectorScopeId?: string | null;
  setConversationId: (id: string) => void;
  clearConversationId: () => void;
  compact?: boolean;
  mode: "work" | "plan";
  onModeChange: (mode: "work" | "plan") => void;
  planMarkdown: string;
  railTitle: string;
  railSurface: AgentRailSurface;
  agenticMode: AgenticTaskMode;
  onAgenticModeChange: (mode: AgenticTaskMode) => void;
  lastAgenticTaskId: string | null;
  isLoading: boolean;
  thinkingContent?: string;
  pausedTaskId: string | null;
  pausingTaskId: string | null;
  onResumeActiveRun: () => Promise<void>;
  onQueueActiveRun: (
    message: string,
    queueId?: string,
    context?: QueuedRunContext,
  ) => Promise<void>;
  onSteerActiveRun: (
    message: string,
    queueId?: string,
    context?: QueuedRunContext,
  ) => Promise<void>;
  onUpdateQueuedRun: (queueId: string, message: string) => Promise<void>;
  onRemoveQueuedRun: (queueId: string) => Promise<void>;
}) {
  const { activeAgent, agents } = useFintheonAgents();
  const { addToast } = useToast();
  const runtime = useThreadRuntime();
  const assistantUiRunning = useThread((t) => t.isRunning);
  const isRunning = isLoading || assistantUiRunning;
  const threadMessages = useThread((t) => t.messages);

  // Build dynamic display names — CAO name comes from agent context
  const agentNames: Record<string, string> = useMemo(() => {
    const cao = agents.find((a) => a.id === "harper");
    return { ...AGENT_NAMES_DEFAULT, harper: cao?.name ?? "Harper" };
  }, [agents]);

  const [activeSkill, setActiveSkill] = useState<string | null>(null);
  const [showSkills, setShowSkills] = useState(false);
  const [showWorkDrawer, setShowWorkDrawer] = useState(false);
  const [approvalDrawerCollapsed, setApprovalDrawerCollapsed] = useState(false);
  const [displayedPlanActionId, setDisplayedPlanActionId] = useState<
    string | null
  >(null);
  const previousWorkItemCountRef = useRef(0);
  const previousQueueLengthRef = useRef(0);
  const hasMountedWorkDrawerRef = useRef(false);
  const drawerStateHydratedKeyRef = useRef<string | null>(null);
  const [hasChatStarted, setHasChatStarted] = useState(false);
  const [composerDraftRequest, setComposerDraftRequest] = useState<
    string | null
  >(null);
  const [solvysDrawerMessages, setSolvysDrawerMessages] = useState<
    SolvysChatDrawerMessage[]
  >([]);
  const [solvysDrawerOpen, setSolvysDrawerOpen] = useState(false);
  const [agenticProjection, setAgenticProjection] =
    useState<AgenticProjection | null>(null);

  // SSE: subscribe to surface.sidebar for cross-agent findings
  const { lastEvent: sidebarEvent } = useAgentBusSSE<SidebarNotifyEvent>({
    url: `${API_BASE}/api/dag/surface/sidebar`,
    enabled: true,
    reconnect: true,
  });

  useEffect(() => {
    if (!sidebarEvent || sidebarEvent.type !== "agent-finding") return;
    // Suppress if the user is already chatting with this agent
    const agentFintheonId = AGENT_TO_FINTHEON_ID[sidebarEvent.agentId];
    if (agentFintheonId && activeAgent?.id === agentFintheonId) return;

    const message: SolvysChatDrawerMessage = {
      id: `${sidebarEvent.dagId}-${sidebarEvent.agentId}-${Date.now()}`,
      agentName: agentNames[sidebarEvent.agentId] ?? sidebarEvent.agentId,
      summary: sidebarEvent.summary,
    };

    setSolvysDrawerMessages((prev) => [...prev.slice(-5), message]);
    setSolvysDrawerOpen(true);
  }, [sidebarEvent, activeAgent?.id, agentNames]);

  useEffect(() => {
    if (!conversationId) {
      setAgenticProjection(null);
      return;
    }
    let cancelled = false;
    let timer: number | undefined;
    const refreshProjection = () => {
      fetchAgenticProjection({ conversationId })
        .then((projection) => {
          if (cancelled) return;
          setAgenticProjection(projection);
          if (projection.activeTask) {
            timer = window.setTimeout(refreshProjection, 1500);
          }
        })
        .catch(() => {
          if (!cancelled) setAgenticProjection(null);
        });
    };
    timer = window.setTimeout(refreshProjection, isRunning ? 800 : 0);
    return () => {
      cancelled = true;
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [conversationId, isRunning, lastAgenticTaskId]);

  const clearSolvysDrawerMessages = useCallback(() => {
    setSolvysDrawerMessages([]);
    setSolvysDrawerOpen(false);
  }, []);
  const openSolvysDrawer = useCallback(() => setSolvysDrawerOpen(true), []);
  const collapseSolvysDrawer = useCallback(
    () => setSolvysDrawerOpen(false),
    [],
  );

  const sendNow = useCallback(
    (msg: string, option?: AgenticTaskMode | QueuedMessage) => {
      const mode = typeof option === "string" ? option : undefined;
      const queued = typeof option === "object" ? option : undefined;
      setHasChatStarted(true);
      runtime.append({
        role: "user",
        content: [
          { type: "text", text: msg },
          ...(queued?.images ?? []).map((image) => ({
            type: "image" as const,
            image,
          })),
        ],
        ...(mode || queued?.referenceContext || queued?.riskFlowContext
          ? {
              metadata: {
                custom: {
                  ...(mode ? { agenticMode: mode } : {}),
                  ...(queued?.referenceContext ?? {}),
                  ...(queued?.riskFlowContext
                    ? { riskFlowContext: queued.riskFlowContext }
                    : {}),
                },
              },
            }
          : {}),
      });
    },
    [runtime],
  );
  const workStorageParts = useMemo(
    () => [
      "sidebar-chat",
      activeAgent?.id ?? "default",
      conversationWorkStoragePart(conversationId),
    ],
    [activeAgent?.id, conversationId],
  );
  const draftWorkStorageParts = useMemo(
    () => [
      "sidebar-chat",
      activeAgent?.id ?? "default",
      conversationWorkStoragePart(undefined),
    ],
    [activeAgent?.id],
  );
  const todoStorageKey = useMemo(
    () => buildChatWorkStorageKey("todo-list", workStorageParts),
    [workStorageParts],
  );
  const draftTodoStorageKey = useMemo(
    () => buildChatWorkStorageKey("todo-list", draftWorkStorageParts),
    [draftWorkStorageParts],
  );
  const queueStorageKey = useMemo(
    () => buildChatWorkStorageKey("message-queue", workStorageParts),
    [workStorageParts],
  );
  const drawerStateStorageKey = useMemo(
    () => buildChatWorkStorageKey("drawer-state", workStorageParts),
    [workStorageParts],
  );
  const {
    queue,
    addQueue,
    markServerOwned,
    editQueue,
    removeQueue,
    steerQueue,
    queueMessage,
    pullQueueToDraft,
    reorderQueue,
    sendOne,
    clearQueue,
    isStorageLoaded: queueLoaded,
  } = useMessageQueue({
    isRunning,
    sendNow,
    storageKey: queueStorageKey,
    conversationId,
  });

  const {
    todos,
    addTodo,
    toggleTodo,
    removeTodo,
    clearTodos,
    isStorageLoaded: todosLoaded,
  } = useTodoList(todoStorageKey, { isIdleTimerPaused: isRunning });
  const clearConversationTodos = useCallback(() => {
    clearTodos({ extraStorageKeys: [draftTodoStorageKey] });
  }, [clearTodos, draftTodoStorageKey]);
  const workItemCount = todos.length + queue.length;
  const hasWorkDrawerContent = workItemCount > 0;
  const workStorageLoaded = todosLoaded && queueLoaded;

  useEffect(() => {
    if (!workStorageLoaded) return;
    if (drawerStateHydratedKeyRef.current === drawerStateStorageKey) return;
    drawerStateHydratedKeyRef.current = drawerStateStorageKey;
    hasMountedWorkDrawerRef.current = true;
    previousWorkItemCountRef.current = workItemCount;
    let savedOpen = false;
    try {
      savedOpen = sessionStorage.getItem(drawerStateStorageKey) === "open";
    } catch {
      savedOpen = false;
    }
    setShowWorkDrawer(savedOpen && workItemCount > 0);
  }, [drawerStateStorageKey, workItemCount, workStorageLoaded]);

  useEffect(() => {
    if (!workStorageLoaded) return;
    try {
      if (showWorkDrawer && hasWorkDrawerContent) {
        sessionStorage.setItem(drawerStateStorageKey, "open");
      } else {
        sessionStorage.setItem(drawerStateStorageKey, "closed");
      }
    } catch {
      /* ignore */
    }
  }, [
    drawerStateStorageKey,
    hasWorkDrawerContent,
    showWorkDrawer,
    workStorageLoaded,
  ]);

  const handleTodoUiAction = useCallback(
    (payload: { items: ChatUiTodoItem[] }) => {
      for (const item of payload.items) {
        addTodo(item.text, {
          issueTrackingType: item.issueType,
          source: "harper-ui-tool",
        });
      }
      setShowWorkDrawer(true);
    },
    [addTodo],
  );

  const handlePullQueueToDraft = useCallback(
    (id: string) => {
      const draft = pullQueueToDraft(id);
      if (draft !== null) setComposerDraftRequest(draft);
    },
    [pullQueueToDraft],
  );

  const handleRightRailUiAction = useCallback(
    (payload: ChatUiRightRailPayload) => {
      window.dispatchEvent(
        new CustomEvent("fintheon:agent-plan-rail", {
          detail: {
            open: true,
            title: payload.title,
            surface: toAgentRailSurface(payload.surface),
            markdown: payload.markdown,
            append: payload.append,
          },
        }),
      );
    },
    [],
  );

  const {
    questionnaire,
    planProposal,
    answerWidgets,
    isSubmittingAnswers,
    isSubmittingPlanDecision,
    submitAnswers,
    submitPlanDecision,
  } = useChatUiActions(lastRequestId, {
    surfaceId: "chat",
    onTodoDrawer: handleTodoUiAction,
    onPlanDecisionContinuation: (message, status) =>
      sendNow(message, status === "approved" ? "analyze" : "plan"),
    onQuestionnaireContinuation: (message) => sendNow(message, "analyze"),
    onRightRail: handleRightRailUiAction,
  });

  useEffect(() => {
    if (questionnaire) setApprovalDrawerCollapsed(false);
  }, [questionnaire?.actionId]);

  const planApprovalPending = planProposal?.status === "pending";
  const planApprovalReady =
    !!planApprovalPending && displayedPlanActionId === planProposal?.actionId;

  useEffect(() => {
    if (!planApprovalPending) {
      setDisplayedPlanActionId(null);
      return;
    }
    setDisplayedPlanActionId(null);
    setApprovalDrawerCollapsed(false);
  }, [planProposal?.actionId, planApprovalPending]);

  const handlePlanDisplayComplete = useCallback((actionId: string) => {
    setDisplayedPlanActionId((current) =>
      current === actionId ? current : actionId,
    );
  }, []);

  const handleApprovedPlan = useCallback(
    async (feedback?: string) => {
      onAgenticModeChange("analyze");
      await submitPlanDecision("approved", feedback);
    },
    [onAgenticModeChange, submitPlanDecision],
  );

  const handlePlanRevision = useCallback(
    async (feedback: string) => {
      const instructions =
        feedback.trim() || "Revise the plan before implementation.";
      onAgenticModeChange("plan");
      await submitPlanDecision("denied", instructions);
      sendNow(
        [
          "[Plan revision requested]",
          "Keep this CAO turn in Plan mode. Do not implement yet.",
          "Revise the previous plan using this additional context:",
          "",
          instructions,
        ].join("\n"),
        "plan",
      );
    },
    [onAgenticModeChange, sendNow, submitPlanDecision],
  );

  const approvalDrawerOpen =
    (!!questionnaire || planApprovalReady) && !approvalDrawerCollapsed;
  const workDrawerOpen =
    (showWorkDrawer || queue.length > 0) &&
    hasWorkDrawerContent &&
    !questionnaire &&
    !planApprovalPending;
  const collapseWorkDrawer = useCallback(() => {
    setShowWorkDrawer(false);
  }, []);
  useAutoCollapseDrawer({
    isOpen: workDrawerOpen,
    isAgentActive: isRunning,
    onCollapse: collapseWorkDrawer,
  });
  const pendingTodoCount = todos.filter((todo) => !todo.done).length;
  const doneTodoCount = todos.length - pendingTodoCount;
  const workDrawerWorking = isRunning && pendingTodoCount > 0;
  const drawerPeekSlot =
    questionnaire && approvalDrawerCollapsed ? (
      <ChatDrawerPeek
        tone="action"
        title="Action needed"
        detail={`${questionnaire.questions.length} question${
          questionnaire.questions.length === 1 ? "" : "s"
        } waiting for your answer`}
        onOpen={() => setApprovalDrawerCollapsed(false)}
      />
    ) : planApprovalReady && approvalDrawerCollapsed ? (
      <ChatDrawerPeek
        tone="action"
        title="Action needed"
        detail="Implement the Plan? waiting for your answer"
        onOpen={() => setApprovalDrawerCollapsed(false)}
      />
    ) : todos.length > 0 &&
      !showWorkDrawer &&
      !questionnaire &&
      !planApprovalPending ? (
      <ChatDrawerPeek
        tone="status"
        title="Tasks"
        detail={`${doneTodoCount}/${todos.length} tasks completed`}
        statusActive={workDrawerWorking}
        statusComplete={todos.length > 0 && pendingTodoCount === 0}
        doneCount={doneTodoCount}
        totalCount={todos.length}
        onOpen={() => setShowWorkDrawer(true)}
      />
    ) : null;
  const hasDrawerPeek = !!drawerPeekSlot;
  const settledComposerBottomInset = hasChatStarted ? 104 : 136;
  const settledScrollButtonOffset = hasChatStarted ? 88 : 116;
  const composerBottomInset = approvalDrawerOpen
    ? 336
    : workDrawerOpen
      ? 304
      : hasDrawerPeek
        ? 150
        : settledComposerBottomInset;
  const scrollButtonOffset = approvalDrawerOpen
    ? 312
    : workDrawerOpen
      ? 282
      : settledScrollButtonOffset;

  const handleQueueMessage = useCallback(
    (msg: string, payload?: { images?: string[] }) => {
      const queuePayload = payload?.images?.length
        ? { images: payload.images }
        : undefined;
      const queueId = addQueue(msg, queuePayload);
      if (!queueId || !isRunning) return;
      void onQueueActiveRun(msg, queueId, queuePayload)
        .then(() => markServerOwned(queueId))
        .catch((error) =>
          addToast(
            error instanceof Error
              ? error.message
              : "Message queue sync failed.",
            "error",
          ),
        );
    },
    [addQueue, addToast, isRunning, markServerOwned, onQueueActiveRun],
  );

  const handleSend = useCallback(
    (msg: string) => {
      if (isRunning) {
        handleQueueMessage(msg);
        setHasChatStarted(true);
        return;
      }
      sendNow(msg);
    },
    [handleQueueMessage, isRunning, sendNow],
  );

  const handleSteerQueue = useCallback(
    (id: string) => {
      const item = queue.find((entry) => entry.id === id);
      if (!item) return;
      void onSteerActiveRun(item.text, item.id, queueItemContext(item))
        .then(() => steerQueue(id))
        .catch((error) =>
          addToast(
            error instanceof Error ? error.message : "Run steer failed.",
            "error",
          ),
        );
    },
    [addToast, onSteerActiveRun, queue, steerQueue],
  );

  const handleQueueQueuedMessage = useCallback(
    (id: string) => {
      const item = queue.find((entry) => entry.id === id);
      if (!item) return;
      if (!item.serverOwned) {
        queueMessage(id);
        return;
      }
      void onQueueActiveRun(item.text, item.id, queueItemContext(item))
        .then(() => queueMessage(id))
        .catch((error) =>
          addToast(
            error instanceof Error
              ? error.message
              : "Queued message update failed.",
            "error",
          ),
        );
    },
    [addToast, onQueueActiveRun, queue, queueMessage],
  );

  const handleEditQueue = useCallback(
    (id: string, text: string) => {
      const item = queue.find((entry) => entry.id === id);
      if (!item?.serverOwned) {
        editQueue(id, text);
        return;
      }
      void onUpdateQueuedRun(id, text)
        .then(() => editQueue(id, text))
        .catch((error) =>
          addToast(
            error instanceof Error
              ? error.message
              : "Queued message update failed.",
            "error",
          ),
        );
    },
    [addToast, editQueue, onUpdateQueuedRun, queue],
  );

  const handleRemoveQueue = useCallback(
    (id: string) => {
      const item = queue.find((entry) => entry.id === id);
      if (!item?.serverOwned) {
        removeQueue(id);
        return;
      }
      void onRemoveQueuedRun(id)
        .then(() => removeQueue(id))
        .catch((error) =>
          addToast(
            error instanceof Error
              ? error.message
              : "Queued message removal failed.",
            "error",
          ),
        );
    },
    [addToast, onRemoveQueuedRun, queue, removeQueue],
  );

  const handleSkillSend = useCallback(
    (skillId: string, msg: string, connectorIds: string[] = []) => {
      setActiveSkill(skillId);
      if (connectorIds.length > 0) {
        writeAttachedConnectorIds(connectorScopeId, connectorIds);
      }
      const prefix = SKILL_PREFIXES[skillId] || "";
      handleSend(prefix ? `${prefix}\n\n${msg}` : msg);
    },
    [connectorScopeId, handleSend],
  );

  const clearCurrentChat = useCallback(() => {
    withViewTransition(() => {
      resetActiveHarperProviderToDefault();
      setHasChatStarted(false);
      setShowWorkDrawer(false);
      clearConversationTodos();
      clearQueue();
      clearConversationId();
    });
  }, [clearConversationId, clearConversationTodos, clearQueue]);

  const handleNewChat = useCallback(async () => {
    if (conversationId) {
      try {
        const token = await getAccessToken();
        const response = await fetch(
          `${API_BASE}/api/chat/conversations/${encodeURIComponent(
            conversationId,
          )}/archive`,
          {
            method: "POST",
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          },
        );
        if (!response.ok) throw new Error("Archive failed");
      } catch {
        addToast("The current chat could not be archived.", "error");
        return;
      }
    }
    clearCurrentChat();
  }, [addToast, clearCurrentChat, conversationId]);

  useEffect(() => {
    const handleDeletedConversation = (event: Event) => {
      if (readDeletedConversationId(event) !== conversationId) return;
      clearCurrentChat();
    };
    window.addEventListener(
      CHAT_CONVERSATION_DELETED_EVENT,
      handleDeletedConversation,
    );
    return () =>
      window.removeEventListener(
        CHAT_CONVERSATION_DELETED_EVENT,
        handleDeletedConversation,
      );
  }, [clearCurrentChat, conversationId]);

  const handleOpenConversationHistory = useCallback(() => {
    window.dispatchEvent(new Event("fintheon:open-mobile-drawer"));
  }, []);

  const handleDailySummary = useCallback(() => {
    if (!isRunning) {
      withViewTransition(() => handleSend("Generate today's daily summary."));
    }
  }, [handleSend, isRunning]);

  useEffect(() => {
    if (threadMessages.length > 0) setHasChatStarted(true);
  }, [threadMessages.length]);

  useEffect(() => {
    const onSidebarSend = (event: Event) => {
      const message = (event as CustomEvent<{ message?: unknown }>).detail
        ?.message;
      if (typeof message === "string" && message.trim()) {
        handleSend(message);
      }
    };
    window.addEventListener("fintheon:chat-sidebar-send", onSidebarSend);
    return () =>
      window.removeEventListener("fintheon:chat-sidebar-send", onSidebarSend);
  }, [handleSend]);

  useEffect(() => {
    const onOpenSkill = (event: Event) => {
      const detail = (
        event as CustomEvent<{
          skillId?: unknown;
          prompt?: unknown;
          connectorIds?: unknown;
        }>
      ).detail;
      if (typeof detail?.skillId !== "string") return;
      if (typeof detail.prompt !== "string" || !detail.prompt.trim()) return;
      const connectorIds = Array.isArray(detail.connectorIds)
        ? detail.connectorIds.filter(
            (item): item is string => typeof item === "string",
          )
        : [];
      handleSkillSend(detail.skillId, detail.prompt, connectorIds);
    };
    window.addEventListener("fintheon:open-sidebar-chat-skill", onOpenSkill);
    return () =>
      window.removeEventListener(
        "fintheon:open-sidebar-chat-skill",
        onOpenSkill,
      );
  }, [handleSkillSend]);

  useEffect(() => {
    const previousQueueLength = previousQueueLengthRef.current;
    previousQueueLengthRef.current = queue.length;
    if (previousQueueLength > 0 && todos.length > 0) {
      setShowWorkDrawer(false);
    }
  }, [queue.length, todos.length]);

  useEffect(() => {
    if (!workStorageLoaded) return;
    if (!hasMountedWorkDrawerRef.current) {
      hasMountedWorkDrawerRef.current = true;
      previousWorkItemCountRef.current = workItemCount;
      if (workItemCount === 0) setShowWorkDrawer(false);
      return;
    }
    previousWorkItemCountRef.current = workItemCount;
    if (workItemCount === 0) {
      setShowWorkDrawer(false);
      return;
    }
  }, [workItemCount, workStorageLoaded]);

  // Listen for toolbar events dispatched from ConsiliumHub icons
  useEffect(() => {
    const onRunReport = () => {
      if (!isRunning)
        withViewTransition(() => handleSend("Run the MDB report"));
    };
    const onLoadSession = (e: Event) => {
      const id = (e as CustomEvent).detail?.id;
      console.debug("[ChatSidebar] load-session event received", { id });
      if (id) {
        withViewTransition(() => {
          setShowWorkDrawer(false);
          setHasChatStarted(true);
          setConversationId(id);
        });
      }
    };

    window.addEventListener("fintheon:chat-new", handleNewChat);
    window.addEventListener("fintheon:chat-run-report", onRunReport);
    window.addEventListener("fintheon:chat-load-session", onLoadSession);
    return () => {
      window.removeEventListener("fintheon:chat-new", handleNewChat);
      window.removeEventListener("fintheon:chat-run-report", onRunReport);
      window.removeEventListener("fintheon:chat-load-session", onLoadSession);
    };
  }, [handleSend, handleNewChat, isRunning, setConversationId]);

  return (
    <div
      className={`fintheon-chat-interface-surface relative flex h-full flex-col overflow-hidden bg-transparent transition-[padding-right] duration-300 ${
        !compact && mode === "plan" ? "md:pr-[40%]" : "md:pr-0"
      }`}
      data-agent-chat-surface="sidebar chat"
      data-conversation-id={conversationId || undefined}
    >
      {mode === "plan" && (
        <button
          aria-label="Close plan drawer backdrop"
          className="absolute inset-0 z-30 bg-black/20 lg:hidden"
          onClick={() => onModeChange("work")}
        />
      )}

      <SolvysChatDrawer
        messages={solvysDrawerMessages}
        isOpen={solvysDrawerOpen}
        onOpen={openSolvysDrawer}
        onCollapse={collapseSolvysDrawer}
        onClear={clearSolvysDrawerMessages}
        onSendMessage={handleSend}
      />
      <ChatCanvasActionStrip
        onNewChat={handleNewChat}
        onOpenHistory={handleOpenConversationHistory}
        onDailySummary={handleDailySummary}
      />
      <FintheonThread
        onSend={handleSend}
        isLoading={isRunning}
        showThoughtTrail={isLoading}
        agentName={activeAgent?.name}
        lastError={lastError}
        lastRequestId={lastRequestId}
        thinkingContent={thinkingContent}
        compact={compact}
        hasSubmittedMessage={hasChatStarted}
        scrollButtonOffset={scrollButtonOffset}
        composerBottomInset={composerBottomInset}
        answerWidgets={answerWidgets}
        planProposal={planProposal}
        onPlanDisplayComplete={handlePlanDisplayComplete}
      />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 z-30">
        <FintheonComposer
          compact={compact}
          thinkHarder={thinkHarder}
          setThinkHarder={setThinkHarder}
          reasoningLevel={reasoningLevel}
          onReasoningLevelChange={(level) => {
            setReasoningLevel(level);
            setThinkHarder(shouldThinkHarder(level));
          }}
          lastError={lastError}
          activeSkill={activeSkill}
          onSelectSkill={setActiveSkill}
          showSkills={showSkills}
          onToggleSkills={() => setShowSkills((v) => !v)}
          conversationId={conversationId}
          connectorScopeId={connectorScopeId}
          onConversationGone={clearConversationId}
          onQueueMessage={handleQueueMessage}
          approvalDrawerOpen={approvalDrawerOpen}
          approvalDrawerSlot={
            questionnaire ? (
              <ChatQuestionApprovalDrawer
                questionnaire={questionnaire}
                isSubmitting={isSubmittingAnswers}
                onSubmit={submitAnswers}
                onCancel={() => setApprovalDrawerCollapsed(true)}
              />
            ) : planApprovalReady ? (
              <ChatPlanApprovalQuestionDrawer
                proposal={planProposal}
                isSubmitting={isSubmittingPlanDecision}
                onApprove={handleApprovedPlan}
                onRequestChanges={handlePlanRevision}
                onCancel={() => setApprovalDrawerCollapsed(true)}
              />
            ) : null
          }
          workDrawerOpen={workDrawerOpen}
          workDrawerSlot={
            <TodoDrawer
              isOpen={workDrawerOpen}
              onClose={collapseWorkDrawer}
              todos={todos}
              onToggleTodo={toggleTodo}
              onRemoveTodo={removeTodo}
              queue={queue}
              onEditQueue={handleEditQueue}
              onRemoveQueue={handleRemoveQueue}
              onSteerQueue={handleSteerQueue}
              onQueueQueuedMessage={handleQueueQueuedMessage}
              onPullQueueToDraft={handlePullQueueToDraft}
              onReorderQueue={reorderQueue}
              onSendQueueOne={sendOne}
              showTodos={showWorkDrawer}
              approvalPending={!!questionnaire || planApprovalReady}
              agentActive={isRunning}
            />
          }
          drawerPeekSlot={drawerPeekSlot}
          queueCount={queue.length}
          draftTextRequest={composerDraftRequest}
          onDraftTextRequestConsumed={() => setComposerDraftRequest(null)}
          agenticMode={agenticMode}
          onAgenticModeChange={onAgenticModeChange}
          draftCardsSlot={<AgenticDraftCards projection={agenticProjection} />}
          isLoading={isRunning}
          paused={Boolean(pausedTaskId)}
          pausing={Boolean(pausingTaskId)}
          onResume={onResumeActiveRun}
          onMessageSubmitted={() => setHasChatStarted(true)}
          showSafetyNotice={!hasChatStarted}
        />
      </div>

      <aside
        className={`fintheon-side-surface fintheon-drawer-right absolute right-0 top-0 z-40 h-full border-l border-[var(--fintheon-accent)]/20 bg-[#090704] shadow-2xl transition-transform duration-300 ease-out ${
          mode === "plan" ? "" : "pointer-events-none"
        } w-full md:w-[40%]`}
        aria-label="Agent workspace rail"
        style={{
          transform:
            mode === "plan" ? "translateX(0)" : "translateX(calc(100% + 8px))",
        }}
      >
        <div className="flex h-full flex-col">
          <div className="flex items-center justify-between border-b border-[var(--fintheon-accent)]/20 px-4 py-3">
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-[0.12em] text-zinc-500">
                Agent Workspace
              </p>
              <p className="truncate text-[12px] font-semibold text-[var(--fintheon-accent)]">
                {railTitle}
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <button
                onClick={() =>
                  handleSend(`Begin this plan:\n\n${planMarkdown}`)
                }
                className="rounded border border-[var(--fintheon-accent)]/25 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.1em] text-[var(--fintheon-accent)] transition-colors hover:bg-[var(--fintheon-accent)]/10"
                title="Begin this plan"
              >
                <span className="inline-flex items-center gap-1">
                  <Play size={10} />
                  Begin
                </span>
              </button>
              <button
                onClick={() => onModeChange("work")}
                className="rounded border border-[var(--fintheon-accent)]/30 px-2 py-1 text-[10px] text-[var(--fintheon-accent)] hover:bg-[var(--fintheon-accent)]/10"
              >
                Dismiss
              </button>
            </div>
          </div>
          <div className="flex min-h-0 flex-1 flex-col gap-2 p-3">
            <div className="flex items-center justify-between rounded-md border border-white/[0.06] bg-white/[0.025] px-2 py-1">
              <span className="text-[9px] uppercase tracking-[0.14em] text-[#f0ead6]/42">
                {railSurface}
              </span>
              <span className="text-[9px] uppercase tracking-[0.14em] text-[#f0ead6]/28">
                agent-controlled
              </span>
            </div>
            {railSurface === "report" ? (
              <div
                className="min-h-0 flex-1 overflow-auto rounded-md border border-[var(--fintheon-accent)]/15 bg-[#0d0a06] p-3 text-[12px] leading-5 text-[#f0ead6]"
                aria-label="Agent workspace content"
              >
                <StreamdownChat content={planMarkdown} />
              </div>
            ) : (
              <pre
                className="min-h-0 flex-1 overflow-auto whitespace-pre-wrap rounded-md border border-[var(--fintheon-accent)]/15 bg-[#0d0a06] p-3 font-mono text-[12px] leading-5 text-[#f0ead6] outline-none"
                aria-label="Agent workspace content"
              >
                {planMarkdown}
              </pre>
            )}
          </div>
        </div>
      </aside>
    </div>
  );
}

export function ChatSidebar({ compact = true }: { compact?: boolean } = {}) {
  const { activeAgent } = useFintheonAgents();
  const [thinkHarder, setThinkHarder] = useState(false);
  const [reasoningLevel, setReasoningLevel] = useState<ReasoningLevel>(() => {
    try {
      return normalizeReasoningLevel(
        localStorage.getItem("fintheon:reasoning-level"),
      );
    } catch {
      return "standard";
    }
  });
  const [agenticMode, setAgenticMode] = useState<AgenticTaskMode>(() => {
    try {
      return normalizeAgenticTaskMode(
        localStorage.getItem("fintheon:agentic-mode"),
      );
    } catch {
      return "analyze";
    }
  });
  const {
    runtime,
    conversationId,
    setConversationId,
    clearConversationId,
    lastError,
    connectorScopeId,
    lastRequestId,
    lastAgenticTaskId,
    isLoading,
    thinkingContent,
    pausedTaskId,
    pausingTaskId,
    resumeActiveRun,
    queueActiveRun,
    steerActiveRun,
    updateQueuedRunMessage,
    removeQueuedRunMessage,
  } = useFintheonRuntime(
    activeAgent?.id ?? "default",
    thinkHarder,
    "chat",
    reasoningLevel,
    null,
    agenticMode,
  );
  const [mode, setMode] = useState<"work" | "plan">("work");
  const [railTitle, setRailTitle] = useState("plan.md");
  const [railSurface, setRailSurface] = useState<AgentRailSurface>("plan");
  const [planMarkdown, setPlanMarkdown] = useState<string>(() => {
    try {
      return localStorage.getItem(PLAN_DRAFT_KEY) ?? PLAN_DRAFT_TEMPLATE;
    } catch {
      return PLAN_DRAFT_TEMPLATE;
    }
  });

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Tab" && event.shiftKey && mode === "plan") {
        event.preventDefault();
        setMode("work");
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [mode]);

  useEffect(() => {
    const surfaceTitles: Record<AgentRailSurface, string> = {
      plan: "plan.md",
      canvas: "canvas.md",
      browser: "browser.md",
      report: "report.md",
    };
    const onAgentRail = (event: Event) => {
      const detail = (event as CustomEvent<AgentRailEventDetail>).detail ?? {};
      if (detail.open === false) {
        setMode("work");
        return;
      }
      const nextSurface = detail.surface ?? "plan";
      setRailSurface(nextSurface);
      setRailTitle(detail.title ?? surfaceTitles[nextSurface]);
      const nextMarkdown = detail.markdown;
      if (typeof nextMarkdown === "string") {
        setPlanMarkdown((prev) =>
          detail.append ? `${prev}${nextMarkdown}` : nextMarkdown,
        );
      }
      setMode("plan");
    };

    window.addEventListener("fintheon:agent-plan-rail", onAgentRail);
    return () =>
      window.removeEventListener("fintheon:agent-plan-rail", onAgentRail);
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem("fintheon:reasoning-level", reasoningLevel);
    } catch {
      // no-op
    }
  }, [reasoningLevel]);

  useEffect(() => {
    try {
      localStorage.setItem("fintheon:agentic-mode", agenticMode);
    } catch {
      // no-op
    }
  }, [agenticMode]);

  useEffect(() => {
    try {
      localStorage.setItem(PLAN_DRAFT_KEY, planMarkdown);
    } catch {
      // no-op
    }
  }, [planMarkdown]);

  return (
    <AssistantRuntimeProvider runtime={runtime}>
      <ChatSidebarInner
        lastError={lastError}
        lastRequestId={lastRequestId ?? null}
        thinkHarder={thinkHarder}
        setThinkHarder={setThinkHarder}
        reasoningLevel={reasoningLevel}
        setReasoningLevel={setReasoningLevel}
        conversationId={conversationId}
        connectorScopeId={connectorScopeId}
        setConversationId={setConversationId}
        clearConversationId={clearConversationId}
        compact={compact}
        mode={mode}
        onModeChange={setMode}
        planMarkdown={planMarkdown}
        railTitle={railTitle}
        railSurface={railSurface}
        agenticMode={agenticMode}
        onAgenticModeChange={setAgenticMode}
        lastAgenticTaskId={lastAgenticTaskId}
        isLoading={isLoading}
        thinkingContent={thinkingContent}
        pausedTaskId={pausedTaskId}
        pausingTaskId={pausingTaskId}
        onResumeActiveRun={resumeActiveRun}
        onQueueActiveRun={queueActiveRun}
        onSteerActiveRun={steerActiveRun}
        onUpdateQueuedRun={updateQueuedRunMessage}
        onRemoveQueuedRun={removeQueuedRunMessage}
      />
    </AssistantRuntimeProvider>
  );
}
